import { useEffect, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, MapPin, Upload, Download, CheckCircle2, FileSpreadsheet, ArrowRight, Umbrella, FileText, Shield, DollarSign, UserX } from "lucide-react";
import { CestinoButton } from "../components/CestinoButton";

const EMPTY_EXT_AMM = { cognome: "", nome: "", indirizzo: "", cap: "", citta: "", provincia: "",
  email: "", telefono_fisso: "", cellulare: "", pec: "", codice_fiscale: "", partita_iva: "" };

const EMPTY = { nome: "", indirizzo: "", citta: "Legnano", cap: "20025", provincia: "MI", num_unita: 0, referente: "", codice_fiscale: "", codice_sdi: "", tipo: "Condominio", note: "",
  compagnia_fornitore_id: "", compagnia_nome: "", compagnia_contatti: "", compagnia_email: "", compagnia_pec: "", compagnia_indirizzo: "",
  agenzia_fornitore_id: "", agenzia_nome: "", agenzia_contatti: "", agenzia_email: "", agenzia_pec: "", agenzia_indirizzo: "",
  numero_polizza: "", polizza_decorrenza: "", polizza_scadenza: "",
  // Iter122cg · dati fatturazione dedicati
  partita_iva: "", pec_fatturazione: "", email_fatturazione: "", referente_pagamento: "",
  indirizzo_fatturazione: "", cap_fatturazione: "", citta_fatturazione: "", provincia_fatturazione: "",
  // Iter122k108 · IBAN del condominio (usato in atti di liquidazione sinistri e altri doc)
  iban: "",
  // Iter122k111 · Flag EXT (condominio non più gestito dallo Studio) + dati nuovo amministratore
  ext: false, ext_amministratore: { ...EMPTY_EXT_AMM } };

export default function Condomini() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [assicuratori, setAssicuratori] = useState([]);
  const [assicOpen, setAssicOpen] = useState(false);
  const [assicFile, setAssicFile] = useState(null);
  const [assicResult, setAssicResult] = useState(null);
  const [assicImporting, setAssicImporting] = useState(false);
  // Iter122ez · Modal Fase 4 · condominio "Da attivare"
  const [bloccatoTarget, setBloccatoTarget] = useState(null);
  const apriOAttiva = (c) => {
    if (c.licenza_stato === "da_attivare") { setBloccatoTarget(c); return; }
    navigate(`/proprieta?condominio=${c.id}`);
  };

  const load = () => api.get("/condomini").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/fornitori", { params: { categoria: "Assicurazione", limit: 500 } })
      .then((r) => setAssicuratori(r.data.fornitori || [])).catch(() => {});
  }, []);

  const selectAssic = (kind, fid) => {
    const fo = assicuratori.find((a) => a.id === fid);
    setForm((f) => ({
      ...f,
      [`${kind}_fornitore_id`]: fid,
      [`${kind}_nome`]: fo ? fo.ragione_sociale : f[`${kind}_nome`],
      [`${kind}_email`]: fo ? (fo.email || "") : f[`${kind}_email`],
      [`${kind}_pec`]: fo ? (fo.pec || "") : f[`${kind}_pec`],
      [`${kind}_contatti`]: fo ? (fo.telefono || fo.referente || "") : f[`${kind}_contatti`],
      [`${kind}_indirizzo`]: fo ? [fo.indirizzo, fo.cap, fo.citta, fo.provincia].filter(Boolean).join(", ") : f[`${kind}_indirizzo`],
    }));
  };

  const downloadAssicTemplate = async () => {
    const res = await api.get("/condomini/assicurazione/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "modello_assicurazioni_condomini.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const doAssicImport = async () => {
    if (!assicFile) return;
    setAssicImporting(true); setAssicResult(null);
    try {
      const fd = new FormData();
      fd.append("file", assicFile);
      const { data } = await api.post("/condomini/assicurazione/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAssicResult(data);
      load();
      toast.success(`${data.updated} condomìni aggiornati con i dati assicurativi`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAssicImporting(false); }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/condomini/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "modello_condomini_edifici.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/condomini/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      load();
      toast.success(`${data.created} condomìni importati`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setError(""); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      ...EMPTY, ...c,
      ext: !!c.ext,
      ext_amministratore: { ...EMPTY_EXT_AMM, ...(c.ext_amministratore || {}) },
    });
    setEditId(c.id); setError(""); setOpen(true);
  };
  const setExtAmm = (k, v) => setForm((f) => ({ ...f, ext_amministratore: { ...(f.ext_amministratore || EMPTY_EXT_AMM), [k]: v } }));

  const save = async () => {
    setError("");
    // Iter122ek/Iter123k172 · Validazione campi obbligatori (necessari per attivazione licenza + fatturazione + sinistri)
    const required = {
      nome: "Nome",
      indirizzo: "Indirizzo",
      citta: "Città",
      cap: "CAP",
      provincia: "Provincia",
      codice_fiscale: "Codice fiscale condominiale",
      referente: "Amministratore legale rapp.te",
    };
    const missing = Object.entries(required).filter(([k]) => !((form[k] || "").toString().trim())).map(([, l]) => l);
    if (missing.length) {
      setError(`Compila i campi obbligatori: ${missing.join(", ")}`);
      return;
    }
    try {
      const payload = { ...form, num_unita: parseInt(form.num_unita) || 0 };
      if (editId) await api.put(`/condomini/${editId}`, payload);
      else await api.post("/condomini", payload);
      setOpen(false);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/condomini/${id}`); load(); };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Stabili</h1>
          <InfoHint section="condomini" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="download-cond-template-button" variant="outline" className="h-10 text-sm" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1.5" /> Scarica modello Excel
          </Button>
          <Button data-testid="open-cond-import-button" variant="outline" className="h-10 text-sm" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}>
            <Upload className="w-4 h-4 mr-1.5" /> Importa Excel
          </Button>
          <Button data-testid="open-assic-import-button" variant="outline" className="h-10 text-sm" onClick={() => { setAssicOpen(true); setAssicResult(null); setAssicFile(null); }}>
            <Umbrella className="w-4 h-4 mr-1.5" /> Dati assicurativi
          </Button>
          <Button data-testid="add-condominio-button" onClick={openNew} className="h-10 px-4 text-sm font-bold bg-brand hover:bg-brand-dark">
            <Plus className="w-4 h-4 mr-1.5" /> Aggiungi
          </Button>
          <CestinoButton entity="condomini" onChange={load} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <Card key={c.id} data-testid={`condominio-card-${c.nome}`}
            data-ext={c.ext ? "true" : "false"}
            className={`p-3 border-2 transition-colors relative ${c.ext ? "border-red-500 bg-red-50/40 hover:border-red-600" : "hover:border-[#C98AA0]"}`}>
            {c.ext && (
              <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-extrabold tracking-wider shadow-sm"
                data-testid={`cond-ext-badge-${c.nome}`}
                title="Condominio EXT · non più gestito dallo Studio">
                <UserX className="w-3 h-3" /> EXT
              </div>
            )}
            <div className={`flex items-start gap-2 mb-1.5 ${c.ext ? "pr-14" : ""}`}>
              <div className="bg-blue-100 text-brand rounded-lg p-1 shrink-0"><Building2 className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm sm:text-base leading-tight break-words">{c.nome}</h3>
                <div className="text-xs text-slate-500 flex items-start gap-1 break-words"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span className="break-words">{c.indirizzo}, {c.citta}</span></div>
              </div>
              {/* Iter122dw · Badge stato licenza (visibile principalmente ai trial) */}
              {c.licenza_stato && (
                <div className="shrink-0" data-testid={`cond-licenza-badge-${c.nome}`}>
                  {c.licenza_stato === "attiva_trial" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Attivo
                    </span>
                  )}
                  {c.licenza_stato === "attiva" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold whitespace-nowrap">
                      Attivo
                    </span>
                  )}
                  {c.licenza_stato === "richiesta" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold whitespace-nowrap">
                      Attivazione richiesta
                    </span>
                  )}
                  {c.licenza_stato === "da_attivare" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold whitespace-nowrap" title="Attivalo dalla sezione Impostazioni & Account">
                      Da attivare
                    </span>
                  )}
                </div>
              )}
            </div>
            {c.licenza_stato === "da_attivare" && (
              <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 leading-snug" data-testid={`cond-attivalo-${c.nome}`}>
                Attivalo dalla sezione <a href="/pannello?tab=licenze-condomini" className="underline font-semibold">Impostazioni &amp; Account</a>.
              </div>
            )}
            <div className="text-xs text-slate-500 mb-2 flex items-center gap-2 flex-wrap" data-testid={`cond-ui-${c.nome}`}>
              <span>{c.ui_count || 0} unità · {c.proprietari_count || 0} propr. · {c.altri_count || 0} altri · {c.conduttori_count || 0} cond.</span>
              {/* Iter122iq · Badge presenza dati assicurativi (Sinistri) e fatturazione */}
              {(() => {
                const assicOK = !!((c.compagnia_nome || "").trim() || (c.numero_polizza || "").trim() || (c.polizza_scadenza || "").trim());
                const fattOK = !!((c.partita_iva || "").trim() || (c.pec_fatturazione || "").trim() || (c.email_fatturazione || "").trim());
                return (
                  <span className="ml-auto flex items-center gap-1.5">
                    <span
                      data-testid={`cond-badge-assic-${c.nome}`}
                      data-status={assicOK ? "ok" : "ko"}
                      title={assicOK ? "Dati assicurazione presenti" : "Dati assicurazione mancanti"}
                      className={`inline-flex items-center justify-center rounded-full w-5 h-5 ${assicOK ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300" : "bg-red-100 text-red-700 ring-1 ring-red-300"}`}
                    >
                      <Shield className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    <span
                      data-testid={`cond-badge-fatt-${c.nome}`}
                      data-status={fattOK ? "ok" : "ko"}
                      title={fattOK ? "Dati fatturazione presenti" : "Dati fatturazione mancanti"}
                      className={`inline-flex items-center justify-center rounded-full w-5 h-5 ${fattOK ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300" : "bg-red-100 text-red-700 ring-1 ring-red-300"}`}
                    >
                      <DollarSign className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`cond-rac-link-${c.nome}`} onClick={() => apriOAttiva(c)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline mr-auto">
                {c.licenza_stato === "da_attivare" ? <>Attiva ora <ArrowRight className="w-3.5 h-3.5" /></> : <>Apri nel RAC <ArrowRight className="w-3.5 h-3.5" /></>}
              </button>
              <Button data-testid={`edit-condominio-${c.nome}`} variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(c)}>
                <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Modifica</span>
              </Button>
              <Button data-testid={`delete-condominio-${c.nome}`} variant="outline" size="sm" className="h-8 px-2 text-red-700 border-red-300" onClick={() => remove(c.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{editId ? "Modifica" : "Nuovo"} condominio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nome" required v={form.nome} on={(v) => setForm({ ...form, nome: v })} testid="cond-nome" />
            <Field label="Indirizzo (via e civico)" required v={form.indirizzo} on={(v) => setForm({ ...form, indirizzo: v })} testid="cond-indirizzo" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Città" required v={form.citta} on={(v) => setForm({ ...form, citta: v })} testid="cond-citta" />
              <Field label="CAP" required v={form.cap} on={(v) => setForm({ ...form, cap: v })} testid="cond-cap" />
              <Field label="Prov." required v={form.provincia} on={(v) => setForm({ ...form, provincia: v })} testid="cond-prov" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Codice fiscale condominiale" required v={form.codice_fiscale} on={(v) => setForm({ ...form, codice_fiscale: v })} testid="cond-cf" />
              <Field label="Codice univoco SDI (fatturazione elettronica)" v={form.codice_sdi} on={(v) => setForm({ ...form, codice_sdi: v })} testid="cond-sdi" />
              {/* Iter122k108 · IBAN condominio (utilizzato nei documenti sinistri: atto di liquidazione, quietanze, ecc.) */}
              <Field label="IBAN condominio" v={form.iban} on={(v) => setForm({ ...form, iban: (v || "").toUpperCase().replace(/\s+/g, "") })} testid="cond-iban" placeholder="IT60X0542811101000000123456" />
            </div>
            <Field label="Amministratore legale rapp.te" required v={form.referente} on={(v) => setForm({ ...form, referente: v })} testid="cond-referente" />

            {/* Iter122k111 · Toggle EXT (condominio non più gestito dallo Studio) */}
            <div className={`border-2 rounded-lg p-3 space-y-3 ${form.ext ? "border-red-400 bg-red-50/40" : "border-slate-200 bg-slate-50/40"}`}
              data-testid="cond-ext-section">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="cond-ext-toggle"
                  checked={!!form.ext}
                  onChange={(e) => setForm({ ...form, ext: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-red-600 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${form.ext ? "text-red-700" : "text-slate-700"}`}>
                    <UserX className="w-4 h-4" /> Ext · Non più gestito dallo Studio
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Se attivato il condominio resta in anagrafica Stabili con evidenza <b>rossa</b> e badge
                    <b> EXT</b>, ma non è più considerato in gestione. Rimane disponibile come <b>Cliente</b> o
                    <b> Controparte</b> nell&apos;anagrafica <b>Credius</b> (es. recupero crediti dei compensi
                    dello Studio o consulenza legale nella nuova veste).
                  </div>
                </div>
              </label>

              {form.ext && (
                <div className="pl-8 space-y-3 border-l-2 border-red-300" data-testid="cond-ext-amm-section">
                  <div className="text-sm font-semibold text-red-800">Nuovo amministratore (subentrato nella gestione)</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Cognome" v={form.ext_amministratore?.cognome} on={(v) => setExtAmm("cognome", v)} testid="cond-ext-cognome" />
                    <Field label="Nome" v={form.ext_amministratore?.nome} on={(v) => setExtAmm("nome", v)} testid="cond-ext-nome" />
                  </div>
                  <Field label="Via e civico" v={form.ext_amministratore?.indirizzo} on={(v) => setExtAmm("indirizzo", v)} testid="cond-ext-indirizzo" />
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="CAP" v={form.ext_amministratore?.cap} on={(v) => setExtAmm("cap", v)} testid="cond-ext-cap" />
                    <Field label="Città" v={form.ext_amministratore?.citta} on={(v) => setExtAmm("citta", v)} testid="cond-ext-citta" />
                    <Field label="Prov." v={form.ext_amministratore?.provincia} on={(v) => setExtAmm("provincia", v)} testid="cond-ext-prov" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Email" v={form.ext_amministratore?.email} on={(v) => setExtAmm("email", v)} testid="cond-ext-email" />
                    <Field label="PEC" v={form.ext_amministratore?.pec} on={(v) => setExtAmm("pec", v)} testid="cond-ext-pec" />
                    <Field label="Telefono fisso" v={form.ext_amministratore?.telefono_fisso} on={(v) => setExtAmm("telefono_fisso", v)} testid="cond-ext-tel" />
                    <Field label="Cellulare" v={form.ext_amministratore?.cellulare} on={(v) => setExtAmm("cellulare", v)} testid="cond-ext-cell" />
                    <Field label="Codice fiscale" v={form.ext_amministratore?.codice_fiscale} on={(v) => setExtAmm("codice_fiscale", v)} testid="cond-ext-cf" />
                    <Field label="Partita IVA" v={form.ext_amministratore?.partita_iva} on={(v) => setExtAmm("partita_iva", v)} testid="cond-ext-piva" />
                  </div>
                </div>
              )}
            </div>

            {/* Iter122cg · Dati fatturazione (per emissione pro-forma/fattura al condominio)
                Iter122el · Sempre visibile: è l'admin tenant a scegliere per singolo condominio
                al momento dell'attivazione licenza (intestazione Studio vs Condominio). Questi dati
                servono se sceglierà "intesta al Condominio". */}
            <div className="border-2 border-blue-200 rounded-lg p-3 bg-blue-50/40 space-y-3" data-testid="cond-dati-fatturazione">
              <div className="text-sm font-bold uppercase tracking-wide text-blue-800 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Dati fatturazione (intestazione al Condominio)
              </div>
              <div className="text-xs text-slate-600">
                Compila questi dati se al momento dell&apos;attivazione della licenza (Impostazioni &amp; Account → Le mie licenze Condomìni)
                sceglierai di intestare la fattura al Condominio invece che allo Studio. Se un campo è vuoto viene usato il valore generale del condominio.
                <div className="mt-1 text-blue-800">
                  <b>Nota:</b> l&apos;<b>Email fatturazione</b> è usata anche per l&apos;invio delle <b>note pro-forma Credius</b> (compensi legali) al Cliente della pratica.
                  Se vuota, il sistema usa in ordine: <i>Email principale del condominio → PEC fatturazione → email di contabilità dello Studio</i>.
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Partita IVA (se presente)" v={form.partita_iva} on={(v) => setForm({ ...form, partita_iva: v })} testid="cond-piva" />
                <Field label="Referente pagamento" v={form.referente_pagamento} on={(v) => setForm({ ...form, referente_pagamento: v })} testid="cond-referente-pagamento" />
                <Field label="Email fatturazione (usata per note pro-forma Credius)" v={form.email_fatturazione} on={(v) => setForm({ ...form, email_fatturazione: v })} testid="cond-email-fatt" />
                <Field label="PEC fatturazione" v={form.pec_fatturazione} on={(v) => setForm({ ...form, pec_fatturazione: v })} testid="cond-pec-fatt" />
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-blue-700 font-semibold">Indirizzo fatturazione (solo se diverso da quello del condominio)</summary>
                <div className="mt-2 grid sm:grid-cols-2 gap-3">
                  <Field label="Indirizzo" v={form.indirizzo_fatturazione} on={(v) => setForm({ ...form, indirizzo_fatturazione: v })} testid="cond-ind-fatt" />
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="CAP" v={form.cap_fatturazione} on={(v) => setForm({ ...form, cap_fatturazione: v })} testid="cond-cap-fatt" />
                    <Field label="Città" v={form.citta_fatturazione} on={(v) => setForm({ ...form, citta_fatturazione: v })} testid="cond-citta-fatt" />
                    <Field label="Prov." v={form.provincia_fatturazione} on={(v) => setForm({ ...form, provincia_fatturazione: v })} testid="cond-prov-fatt" />
                  </div>
                </div>
              </details>
            </div>

            <div className="border-2 border-[#EBC5D2] rounded-lg p-3 bg-[#FBEEF3]/40 space-y-3">
              <div className="text-sm font-bold uppercase tracking-wide text-brand flex items-center gap-2">
                <Umbrella className="w-4 h-4" /> Dati assicurativi
              </div>
              <div>
                <Label className="text-sm font-semibold">Compagnia (da anagrafica fornitori cat. Assicurazione)</Label>
                <select data-testid="cond-compagnia-select" value={form.compagnia_fornitore_id || ""} onChange={(e) => selectAssic("compagnia", e.target.value)}
                  className="h-11 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-sm">
                  <option value="">— scegli o compila manualmente —</option>
                  {assicuratori.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nome compagnia" v={form.compagnia_nome} on={(v) => setForm({ ...form, compagnia_nome: v })} testid="cond-compagnia-nome" />
                <Field label="Telefono compagnia" v={form.compagnia_contatti} on={(v) => setForm({ ...form, compagnia_contatti: v })} />
                <Field label="Email compagnia" v={form.compagnia_email} on={(v) => setForm({ ...form, compagnia_email: v })} />
                <Field label="PEC compagnia" v={form.compagnia_pec} on={(v) => setForm({ ...form, compagnia_pec: v })} />
              </div>
              <Field label="Indirizzo compagnia" v={form.compagnia_indirizzo} on={(v) => setForm({ ...form, compagnia_indirizzo: v })} />

              <div>
                <Label className="text-sm font-semibold">Agenzia (da anagrafica fornitori cat. Assicurazione)</Label>
                <select data-testid="cond-agenzia-select" value={form.agenzia_fornitore_id || ""} onChange={(e) => selectAssic("agenzia", e.target.value)}
                  className="h-11 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-sm">
                  <option value="">— scegli o compila manualmente —</option>
                  {assicuratori.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nome agenzia" v={form.agenzia_nome} on={(v) => setForm({ ...form, agenzia_nome: v })} testid="cond-agenzia-nome" />
                <Field label="Telefono agenzia" v={form.agenzia_contatti} on={(v) => setForm({ ...form, agenzia_contatti: v })} />
                <Field label="Email agenzia" v={form.agenzia_email} on={(v) => setForm({ ...form, agenzia_email: v })} />
                <Field label="PEC agenzia" v={form.agenzia_pec} on={(v) => setForm({ ...form, agenzia_pec: v })} />
              </div>
              <Field label="Indirizzo agenzia" v={form.agenzia_indirizzo} on={(v) => setForm({ ...form, agenzia_indirizzo: v })} />

              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="N° polizza" v={form.numero_polizza} on={(v) => setForm({ ...form, numero_polizza: v })} testid="cond-numero-polizza" />
                <Field label="Decorrenza" v={form.polizza_decorrenza} on={(v) => setForm({ ...form, polizza_decorrenza: v })} />
                <Field label="Scadenza" v={form.polizza_scadenza} on={(v) => setForm({ ...form, polizza_scadenza: v })} />
              </div>
            </div>
            <p className="text-sm text-slate-500">Il numero di unità immobiliari viene calcolato automaticamente dalle anagrafiche del RAC collegate a questo condominio.</p>
            {error && <div className="p-3 bg-red-50 text-red-800 rounded-lg text-base">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annulla</Button>
            <Button data-testid="save-condominio-button" onClick={save} className="h-12 bg-brand hover:bg-brand-dark">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><FileSpreadsheet className="w-6 h-6" /> Importa condomìni da Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#FBEEF3] border-2 border-[#F0D9E1] rounded-lg text-base text-slate-700">
              Colonne riconosciute: <b>Tipo, Nome, Cod Fiscale, Indirizzo, CAP, Città, Provincia, num_unita, Referente</b>.
              La sola colonna obbligatoria è <b>Nome</b>. I condomìni con nome già presente vengono saltati. Formati: .xls e .xlsx.
              <Button variant="link" className="px-1 h-auto text-brand" onClick={downloadTemplate}>Scarica il modello</Button>
            </div>
            <Input data-testid="cond-import-file-input" type="file" accept=".xls,.xlsx" className="h-12"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            {importResult && (
              <div className="space-y-2">
                <div className="p-3 bg-green-50 border-2 border-green-200 rounded-lg text-green-800 font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {importResult.created} condomìni importati
                </div>
                {importResult.skipped?.length > 0 && (
                  <div className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-900">
                    <b>Saltati (già presenti): {importResult.skipped.length}</b>
                    <div className="max-h-28 overflow-y-auto mt-1">{importResult.skipped.join(", ")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} className="h-12">Chiudi</Button>
            <Button data-testid="do-cond-import-button" onClick={doImport} disabled={!importFile || importing} className="h-12 bg-brand hover:bg-brand-dark">
              {importing ? "Importazione..." : "Importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assicOpen} onOpenChange={setAssicOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><Umbrella className="w-6 h-6" /> Dati assicurativi da Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#FBEEF3] border-2 border-[#F0D9E1] rounded-lg text-base text-slate-700">
              Scarica il modello: contiene già l'elenco dei tuoi condomìni. Compila le colonne
              <b> Compagnia, Agenzia, Numero polizza, Decorrenza, Scadenza</b> e i recapiti (Tel/Email/PEC/Indirizzo di compagnia e agenzia), poi ricaricalo.
              L'abbinamento avviene per <b>nome condominio</b> e i campi vuoti non sovrascrivono i dati esistenti.
              <Button variant="link" className="px-1 h-auto text-brand" onClick={downloadAssicTemplate}>Scarica il modello assicurazioni</Button>
            </div>
            <Input data-testid="assic-import-file-input" type="file" accept=".xls,.xlsx" className="h-12"
              onChange={(e) => setAssicFile(e.target.files?.[0] || null)} />
            {assicResult && (
              <div className="space-y-2">
                <div className="p-3 bg-green-50 border-2 border-green-200 rounded-lg text-green-800 font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {assicResult.updated} condomìni aggiornati
                </div>
                {assicResult.non_trovati?.length > 0 && (
                  <div className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-900">
                    <b>Nomi non trovati: {assicResult.non_trovati.length}</b>
                    <div className="max-h-28 overflow-y-auto mt-1">{assicResult.non_trovati.join(", ")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssicOpen(false)} className="h-12">Chiudi</Button>
            <Button data-testid="do-assic-import-button" onClick={doAssicImport} disabled={!assicFile || assicImporting} className="h-12 bg-brand hover:bg-brand-dark">
              {assicImporting ? "Importazione..." : "Importa dati assicurativi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122ez · Modal bloccante per condomini "Da attivare" (Fase 4 Q4a) */}
      <Dialog open={!!bloccatoTarget} onOpenChange={(v) => !v && setBloccatoTarget(null)}>
        <DialogContent className="max-w-md" data-testid="cond-bloccato-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Umbrella className="w-6 h-6 text-red-600" /> Licenza non attiva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Il condominio <b>{bloccatoTarget?.nome}</b> non ha ancora una <b>licenza attiva</b>. Le richieste di manutenzione, i preventivi e i sinistri sono bloccati finché non completi l&apos;attivazione.</p>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded text-amber-900 text-xs leading-relaxed">
              <b>Come si attiva?</b> Scegli il piano (Light / Full / Pro+), l&apos;intestatario della fattura e il metodo di pagamento. Con <b>Stripe</b> il pagamento è immediato e l&apos;attivazione automatica; con <b>bonifico</b> il team Ginius attiva il condominio dopo la conferma.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBloccatoTarget(null)} data-testid="cond-bloccato-annulla">Annulla</Button>
            <Button className="bg-brand hover:bg-brand-dark" data-testid="cond-bloccato-attiva-cta"
              onClick={() => { const cid = bloccatoTarget.id; setBloccatoTarget(null); navigate(`/pannello?tab=licenze-condomini&attiva=${cid}`); }}>
              <Umbrella className="w-4 h-4 mr-1" /> Attiva ora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid, required, placeholder }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} className="h-12 mt-1" placeholder={placeholder} />
    </div>
  );
}
